/**
 * The single source of truth for which languages MyPoz speaks.
 *
 * Adding a language is a four-step change and the compiler enforces three of them:
 *   1. add the tag to SUPPORTED_LOCALES and a label to LOCALE_LABELS (this file),
 *   2. create packages/i18n/src/locales/<tag>/{common,nav}.json,
 *   3. register it in the `resources` map in i18n.ts,
 *   4. map it to a BCP-47 tag in INTL_LOCALES for Intl formatting.
 */

export const SUPPORTED_LOCALES = ['en', 'zh', 'ms'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Namespaces must match the JSON filenames under locales/<tag>/. */
export const NAMESPACES = ['common', 'nav'] as const;
export const DEFAULT_NAMESPACE = 'common';

/** localStorage key the language detector reads and writes. */
export const LOCALE_STORAGE_KEY = 'mypoz_locale';

/** Shown in the language switcher — always in the language itself, never translated. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  zh: '简体中文',
  ms: 'Bahasa Malaysia',
};

/**
 * i18next uses short tags; Intl wants full BCP-47. Kept separate so number/date formatting can
 * be region-specific (Malaysia here) without affecting which catalog loads.
 */
export const INTL_LOCALES: Record<Locale, string> = {
  en: 'en-MY',
  zh: 'zh-Hans-MY',
  ms: 'ms-MY',
};

export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Narrows anything i18next reports (e.g. `en-US`, `zh-CN`) down to a locale we actually have. */
export function normalizeLocale(value: string | undefined): Locale {
  if (!value) return DEFAULT_LOCALE;
  if (isSupportedLocale(value)) return value;
  const base = value.split('-')[0];
  return base && isSupportedLocale(base) ? base : DEFAULT_LOCALE;
}
